/**
 * NotFoundEmptyState
 *
 * Drop-in empty-state for 404 / no-results / route-not-found screens.
 * Visual language matches EmptyInbox and SyncEmptyState exactly:
 *   – same #e8e4f8 card  – same decorative dot positions  – same sparkle stars
 *   – same text-block pattern  – same actionBtn slot
 *
 * Props:
 *   title     – heading text           (string)
 *   subtitle  – sub-text              (string)
 *   width     – svg width             (string | number, default "100%")
 *   height    – svg height            (string | number, default "auto")
 *   actionBtn – optional CTA element  (React.ReactNode)
 */

export const NotFoundEmptyState = ({
  title = "Nothing here",
  subtitle = "We couldn't find what you're looking for",
  width = "100%",
  height = "auto",
  actionBtn,
}: {
  title?: string;
  subtitle?: string;
  width?: string | number;
  height?: string | number;
  actionBtn?: React.ReactNode;
}) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
      <svg
        width={width}
        height={height}
        viewBox="0 0 680 320"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        aria-label={title}
      >
        <title>{title}</title>
        <desc>Magnifying glass with nothing inside, indicating a not-found result</desc>

        {/* ── Background card (identical to sibling components) ─────────── */}
        <rect x="190" y="60" width="300" height="200" rx="14" fill="#e8e4f8" />
        <rect x="190" y="60" width="300" height="200" rx="14" fill="none" stroke="#c4baf0" strokeWidth="1.5" />

        {/* ── Floating ghost accent ──────────────────────────────────────── */}
        {/*
          Sits above the card, same role as the paper-plane in EmptyInbox.
          Ghost body: rounded arc head + scalloped 3-bump bottom.
          Arc:  M(0,20) → A rx=17 ry=18 sweep=1 → (34,20)  [over the top]
          Body: L(34,42) then three Q-bumps going left, Z closes left side.
        */}
        <g transform="translate(406, 16) rotate(-8)">
          <path
            d="M 0 20 A 17 18 0 0 1 34 20 L 34 42 Q 28.5 47 23 42 Q 17 47 11 42 Q 5.5 47 0 42 Z"
            fill="#a99ee0"
            opacity="0.65"
          />
          {/* Eyes */}
          <circle cx="12" cy="25" r="3.5" fill="#7c6fcd" opacity="0.75" />
          <circle cx="22" cy="25" r="3.5" fill="#7c6fcd" opacity="0.75" />
          {/* Surprised-O mouth */}
          <circle cx="17" cy="34" r="2.5" fill="#7c6fcd" opacity="0.60" />
        </g>

        {/* ── Magnifying glass ──────────────────────────────────────────── */}
        {/*
          Lens center: (318, 148), r=46.
          The lens is offset 22px LEFT of the card centre (340) so the handle
          has room to extend toward the bottom-right — same visual logic as
          the envelope-flap offset in EmptyInbox.

          Handle start  ≈ lens edge at 45°:
            (318 + 46·cos45°, 148 + 46·sin45°) ≈ (350.5, 180.5)
          Handle end    : (385, 215) — still within the card (x:190-490, y:60-260)
          Handle is drawn BEFORE the lens so the white lens fill occludes
          the portion of the line that would sit inside the circle.
        */}

        {/* Handle */}
        <line x1="350" y1="180" x2="385" y2="215" stroke="#7c6fcd" strokeWidth="8" strokeLinecap="round" />

        {/* Lens — main circle */}
        <circle cx="318" cy="148" r="46" fill="#ffffff" stroke="#7c6fcd" strokeWidth="5" />

        {/* Lens — inner rim (depth) */}
        <circle cx="318" cy="148" r="40" fill="none" stroke="#c4baf0" strokeWidth="1" opacity="0.45" />

        {/* Lens — glass-shine highlight (top-left) */}
        <ellipse cx="302" cy="133" rx="9" ry="5" fill="#ffffff" opacity="0.45" transform="rotate(-30 302 133)" />

        {/* ── "?" mark inside lens ──────────────────────────────────────── */}
        {/*
          All coordinates verified to sit within r=40 inner rim.
          Path traces: bottom-left of arc → up → over → right → curve down → stem.
        */}
        <path
          d="M 311 136 Q 311 127 318 127 Q 325 127 325 134 Q 325 141 318 144 L 318 151"
          fill="none"
          stroke="#a99ee0"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Dot */}
        <circle cx="318" cy="157" r="2.5" fill="#a99ee0" />

        {/* ── Decorative dots — left (same positions as siblings) ───────── */}
        <circle cx="168" cy="95" r="4" fill="#c4baf0" opacity="0.50" />
        <circle cx="155" cy="112" r="2.5" fill="#c4baf0" opacity="0.35" />
        <circle cx="174" cy="126" r="1.8" fill="#c4baf0" opacity="0.25" />

        {/* ── Decorative dots — right ───────────────────────────────────── */}
        <circle cx="514" cy="168" r="4" fill="#c4baf0" opacity="0.50" />
        <circle cx="528" cy="153" r="2.5" fill="#c4baf0" opacity="0.35" />
        <circle cx="520" cy="184" r="1.8" fill="#c4baf0" opacity="0.25" />

        {/* ── Sparkle stars (same positions as siblings) ────────────────── */}
        <path d="M450 35 L452 41 L458 43 L452 45 L450 51 L448 45 L442 43 L448 41 Z" fill="#a99ee0" opacity="0.55" />
        <path
          d="M218 35 L219.5 39 L224 40.5 L219.5 42 L218 46 L216.5 42 L212 40.5 L216.5 39 Z"
          fill="#a99ee0"
          opacity="0.40"
        />
        {/* Extra small sparkle — upper-right area */}
        <path
          d="M526 88 L527.2 91.8 L531 93 L527.2 94.2 L526 98 L524.8 94.2 L521 93 L524.8 91.8 Z"
          fill="#c4baf0"
          opacity="0.35"
        />
      </svg>

      {/* ── Text block (identical pattern to siblings, with actionBtn slot) */}
      {(title || subtitle || actionBtn) && (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          {title && (
            <p
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 400,
                color: "var(--color-text-secondary, #888)",
              }}
            >
              {title}
            </p>
          )}
          {subtitle && (
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 13,
                fontWeight: 400,
                color: "var(--color-text-tertiary, #aaa)",
              }}
            >
              {subtitle}
            </p>
          )}
          {actionBtn && <div style={{ marginTop: 12 }}>{actionBtn}</div>}
        </div>
      )}
    </div>
  );
};

/* ── Demo wrapper ────────────────────────────────────────────────────────── */
// export default function App() {
//   return (
//     <div
//       style={{
//         minHeight: "100vh",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         gap: 32,
//         flexWrap: "wrap",
//         padding: 24,
//         background: "#f8f7ff",
//         fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
//       }}
//     >
//       {/* Narrow panel */}
//       <div
//         style={{
//           background: "#fff",
//           borderRadius: 16,
//           padding: "32px 20px",
//           width: 280,
//           boxShadow: "0 4px 32px rgba(124,111,205,0.10)",
//           border: "1px solid #ede9ff",
//         }}
//       >
//         <NotFoundEmptyState />
//       </div>

//       {/* Wider panel with actionBtn */}
//       <div
//         style={{
//           background: "#fff",
//           borderRadius: 16,
//           padding: "40px 32px",
//           width: 440,
//           boxShadow: "0 4px 32px rgba(124,111,205,0.10)",
//           border: "1px solid #ede9ff",
//         }}
//       >
//         <NotFoundEmptyState
//           title="Page not found"
//           subtitle="The link may be broken or the page may have been removed."
//           actionBtn={
//             <button
//               style={{
//                 padding: "6px 18px",
//                 fontSize: 13,
//                 fontWeight: 500,
//                 borderRadius: 8,
//                 border: "1px solid #c4baf0",
//                 background: "transparent",
//                 color: "#7c6fcd",
//                 cursor: "pointer",
//               }}
//             >
//               Go back
//             </button>
//           }
//         />
//       </div>
//     </div>
//   );
// }
